/**
 * NocturneAuthShell — Preset 2 shell for auth / marketing / rules / legal.
 *
 * v2 cleanup:
 *  - Left panel now carries a single calm editorial line (no promise list that
 *    competed with the form's own H1).
 *  - Removed dev footer "preset_2 · nocturne · 2026".
 *  - On mobile, the left panel disappears completely and the right content
 *    gets more breathing room.
 */
import { Link, useLocation } from "react-router-dom";

const ARTICLE_PREFIXES = ["/terms", "/privacy", "/rules"];

export default function NocturneAuthShell({ children }) {
    const { pathname } = useLocation();
    const isArticle = ARTICLE_PREFIXES.some((p) => pathname.startsWith(p));
    return (
        <div
            className="min-h-screen flex flex-col md:flex-row"
            style={{ background: "var(--preset-bg)" }}
            data-preset-shell="nocturne_auth"
            data-testid="nocturne-auth-shell"
        >
            {/* Left brand panel — hidden for article-style paths so they get the full width */}
            {!isArticle && (
                <aside
                    className="hidden md:flex md:w-5/12 lg:w-1/2 preset2-hairline-right relative flex-col p-10"
                    style={{ background: "var(--preset-bg)" }}
                >
                    <Link
                        to="/"
                        className="flex items-center gap-2 preset2-serif"
                        style={{
                            fontSize: 20,
                            lineHeight: 1,
                            color: "var(--preset-text-primary)",
                        }}
                        data-testid="nocturne-auth-logo"
                    >
                        <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: "var(--preset-accent)" }}
                        />
                        Juwa
                    </Link>

                    <div className="flex-1 flex items-center">
                        <div className="max-w-sm">
                            <p className="preset2-eyebrow">a quieter house</p>
                            <p
                                className="preset2-serif mt-4"
                                style={{
                                    fontSize: "clamp(22px, 2.6vw, 30px)",
                                    lineHeight: 1.2,
                                    color: "var(--preset-text-secondary)",
                                }}
                            >
                                A quieter house for players who think in years —
                                not in jackpots.
                            </p>
                        </div>
                    </div>

                    <Link
                        to="/"
                        className="preset2-eyebrow preset2-link"
                        style={{ textDecoration: "none" }}
                    >
                        ← back to home
                    </Link>
                </aside>
            )}

            <section
                className={
                    isArticle
                        ? "flex-1 min-w-0"
                        : "flex-1 min-w-0 px-6 py-10 md:px-16 md:py-14 flex flex-col"
                }
                data-testid="nocturne-auth-content"
            >
                {!isArticle && (
                    <Link
                        to="/"
                        className="md:hidden flex items-center gap-2 preset2-serif mb-6"
                        style={{
                            fontSize: 18,
                            color: "var(--preset-text-primary)",
                        }}
                    >
                        <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: "var(--preset-accent)" }}
                        />
                        Juwa
                    </Link>
                )}
                {isArticle ? (
                    <>
                        {/* Tiny brand strip for article routes */}
                        <header
                            className="preset2-hairline-bottom sticky top-0 z-20"
                            style={{ background: "var(--preset-bg)" }}
                        >
                            <div className="max-w-4xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
                                <Link
                                    to="/"
                                    className="flex items-center gap-2 preset2-serif"
                                    style={{
                                        fontSize: 18,
                                        color: "var(--preset-text-primary)",
                                    }}
                                >
                                    <span
                                        className="w-2 h-2 rounded-full"
                                        style={{
                                            background: "var(--preset-accent)",
                                        }}
                                    />
                                    Juwa
                                </Link>
                                <Link
                                    to="/"
                                    className="preset2-eyebrow preset2-link"
                                    style={{ textDecoration: "none" }}
                                >
                                    ← home
                                </Link>
                            </div>
                        </header>
                        {children}
                    </>
                ) : (
                    <div className="w-full max-w-md">{children}</div>
                )}
            </section>
        </div>
    );
}
